import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Collapse,
  Divider,
  LinearProgress,
  Tooltip,
  makeStyles,
  Button,
  Fade,
} from "@material-ui/core";
import {
  CheckCircle,
  Cancel,
  ExpandMore,
  ExpandLess,
  QueueMusicOutlined,
  TrendingUp,
  Send,
  LoyaltyOutlined
} from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import MarkdownWrapper from "../MarkdownWrapper";

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 400,
    minWidth: 300,
    maxHeight: 500,
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.paper,
    borderLeft: `3px solid ${theme.palette.warning.main}`,
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerIcon: {
    marginRight: theme.spacing(1),
    fontSize: "1.2rem",
  },
  suggestionsList: {
    flex: 1,
    overflow: "auto",
    maxHeight: 350,
    ...theme.scrollbarStyles,
  },
  suggestionItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    "&:last-child": {
      borderBottom: "none",
    },
  },
  suggestionContent: {
    "& .MuiListItemText-primary": {
      fontSize: "0.875rem",
      lineHeight: 1.4,
    },
    "& .MuiListItemText-secondary": {
      fontSize: "0.75rem",
      marginTop: theme.spacing(0.5),
    },
  },
  confidenceChip: {
    height: 20,
    fontSize: "0.65rem",
    marginRight: theme.spacing(0.5),
  },
  highConfidence: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  mediumConfidence: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  lowConfidence: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  acceptButton: {
    color: theme.palette.success.main,
    padding: theme.spacing(0.5),
  },
  rejectButton: {
    color: theme.palette.error.main,
    padding: theme.spacing(0.5),
  },
  sendButton: {
    color: theme.palette.primary.main,
    padding: theme.spacing(0.5),
  },
  noSuggestions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  loadingContainer: {
    padding: theme.spacing(2),
  },
  suggestion: {
    marginBottom: theme.spacing(1),
  },
  expandButton: {
    padding: theme.spacing(0.5),
  },
}));

const AgentSuggestionsPanel = ({
  suggestions = [],
  isLoading = false,
  onAcceptSuggestion,
  onRejectSuggestion,
  onSendSuggestion,
  className,
  ...props
}) => {
  const classes = useStyles();
  const [expandedItems, setExpandedItems] = useState(new Set());

  const handleExpandClick = (suggestionId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(suggestionId)) {
      newExpanded.delete(suggestionId);
    } else {
      newExpanded.add(suggestionId);
    }
    setExpandedItems(newExpanded);
  };

  const getConfidenceConfig = (confidence) => {
    if (confidence >= 0.8) {
      return {
        label: i18n.t("agentSuggestions.confidence.high"),
        className: classes.highConfidence,
      };
    } else if (confidence >= 0.5) {
      return {
        label: i18n.t("agentSuggestions.confidence.medium"),
        className: classes.mediumConfidence,
      };
    } else {
      return {
        label: i18n.t("agentSuggestions.confidence.low"),
        className: classes.lowConfidence,
      };
    }
  };

  const formatSuggestionText = (text) => {
    if (!text || typeof text !== 'string') {
      return '';
    }
    if (text.length > 150) {
      return text.substring(0, 150) + "...";
    }
    return text;
  };

  if (isLoading) {
    return (
      <Paper className={`${classes.root} ${className}`} {...props}>
        <Box className={classes.header}>
          <QueueMusicOutlined className={classes.headerIcon} />
          <Typography variant="subtitle2">
            {i18n.t("agentSuggestions.generating")}
          </Typography>
        </Box>
        <Box className={classes.loadingContainer}>
          <LinearProgress />
          <Typography variant="body2" align="center" style={{ marginTop: 8 }}>
            {i18n.t("agentSuggestions.analyzing")}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Fade in={true}>
      <Paper className={`${classes.root} ${className}`} {...props}>
        <Box className={classes.header}>
          <QueueMusicOutlined className={classes.headerIcon} />
          <Typography variant="subtitle2">
            {i18n.t("agentSuggestions.title")}
          </Typography>
          {suggestions.length > 0 && (
            <Chip
              size="small"
              label={suggestions.length}
              style={{ marginLeft: "auto" }}
            />
          )}
        </Box>

        {suggestions.length === 0 ? (
          <Box className={classes.noSuggestions}>
            <LoyaltyOutlined color="disabled" />
            <Typography variant="body2" align="center">
              {i18n.t("agentSuggestions.noSuggestions")}
            </Typography>
          </Box>
        ) : (
          <List className={classes.suggestionsList} dense>
            {suggestions.map((suggestion) => {
              const confidenceConfig = getConfidenceConfig(suggestion.confidence);
              const isExpanded = expandedItems.has(suggestion.id);

              return (
                <ListItem
                  key={suggestion.id}
                  className={classes.suggestionItem}
                  alignItems="flex-start"
                >
                  <Box width="100%">
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Chip
                        size="small"
                        label={confidenceConfig.label}
                        className={`${classes.confidenceChip} ${confidenceConfig.className}`}
                      />
                      <Chip
                        size="small"
                        label={suggestion.type || "Response"}
                        variant="outlined"
                      />
                      {(suggestion.message && suggestion.message.length > 150) && (
                        <IconButton
                          size="small"
                          onClick={() => handleExpandClick(suggestion.id)}
                          className={classes.expandButton}
                        >
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      )}
                    </Box>

                    <Box className={classes.suggestion}>
                      <Typography variant="body2" component="div">
                        <MarkdownWrapper>
                          {isExpanded
                            ? (suggestion.message || '')
                            : formatSuggestionText(suggestion.message || '')}
                        </MarkdownWrapper>
                      </Typography>
                    </Box>

                    {suggestion.context && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                        style={{ marginTop: 4 }}
                      >
                        {i18n.t("agentSuggestions.context")}: {suggestion.context}
                      </Typography>
                    )}

                    <Box className={classes.actionButtons}>
                      <Tooltip title={i18n.t("agentSuggestions.accept")}>
                        <IconButton
                          size="small"
                          className={classes.acceptButton}
                          onClick={() => onAcceptSuggestion?.(suggestion)}
                        >
                          <CheckCircle />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={i18n.t("agentSuggestions.sendDirectly")}>
                        <IconButton
                          size="small"
                          className={classes.sendButton}
                          onClick={() => onSendSuggestion?.(suggestion)}
                        >
                          <Send />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={i18n.t("agentSuggestions.reject")}>
                        <IconButton
                          size="small"
                          className={classes.rejectButton}
                          onClick={() => onRejectSuggestion?.(suggestion)}
                        >
                          <Cancel />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>
    </Fade>
  );
};

export default AgentSuggestionsPanel;